import type {BumpSockets} from "#material/nodes/vector/bump/definition"
import {bumpDefinition} from "#material/nodes/vector/bump/definition"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import {registerMaterialGraphNode} from "#material/register"
import {asVec3, threeConvert, threeNormal, threeValue} from "#material/three/three-utils"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {cyclesVec3} from "#material/cycles/helpers"
import type {ThreeReturns} from "#material/sockets/three"
import type {BumpNode} from "#material/cycles/types"
import {threeBumpMap, threeBumpMapFromTexture} from "#material/nodes/vector/bump/constants"
import type {Three as THREE} from "#material/three/exports"
import {ThreeTsl as TSL} from "#material/three/exports"
import {isTextureNodeLike} from "@cm/three-texture-streaming"

@registerMaterialGraphNode
export class Bump extends MaterialNode<BumpSockets> {
    public override get definition() {
        return bumpDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<BumpSockets>> => {
        const strength = await evaluator.get(this.parameters.inputs.strength)
        const strengthValue = strength?.toFloat() ?? threeConvert(this.parameters.settings.strength, threeValue) ?? threeValue(1.0)

        const heightResult = await evaluator.get(this.parameters.inputs.height)
        if (heightResult === null || heightResult === undefined) {
            return {normal: threeNormal()}
        }

        const heightNode = heightResult as THREE.Node
        if (isTextureNodeLike(heightNode)) {
            const uv = (heightNode.uvNode ?? TSL.uv(0)).xy as THREE.Node<"vec2">
            return {normal: asVec3(threeBumpMapFromTexture(heightNode, uv, strengthValue))}
        }

        return {normal: asVec3(threeBumpMap(heightResult.toFloat(), strengthValue))}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<BumpSockets>> => {
        const bumpNode: BumpNode = {
            type: "bump",
            inputs: await evaluator.getAll({
                distance: this.parameters.inputs.distance,
                height: this.parameters.inputs.height,
                normal: this.parameters.inputs.normal,
                strength: this.parameters.inputs.strength,
                sample_center: undefined,
                sample_x: undefined,
                sample_y: undefined,
            }),
            parameters: {
                invert: this.parameters.settings.invert,
                distance: this.parameters.settings.distance,
                height: this.parameters.settings.height,
                normal: cyclesVec3(this.parameters.settings.normal),
                strength: this.parameters.settings.strength,
                use_object_space: undefined,
                sample_center: undefined,
                sample_x: undefined,
                sample_y: undefined,
            },
            resources: {},
        }

        return {normal: [bumpNode, "normal"]}
    }
}
